import { DeliveryModel, Journey } from '@audibene/ta.utils-opportunity'
import { GlobalStyles, SelectChangeEvent } from '@mui/material'
import { registerLicense } from '@syncfusion/ej2-base'
import { Inject, PopupOpenEventArgs, ScheduleComponent, Week, WorkWeek } from '@syncfusion/ej2-react-schedule'
import { addMinutes, endOfDay, startOfDay } from 'date-fns'
import { zonedTimeToUtc } from 'date-fns-tz'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { config } from '../../../config'
import { AppointmentStatus, AppointmentType, AvailabilityType, NonDeprecatedSolution, Solution, ToastSubType } from '../../../constants'
import {
  useAppointmentTypeOptions,
  useAvailabilitiesView,
  useCountry,
  useLocale,
  useNotification,
  useResource,
  useSearchParameters,
  useShops,
} from '../../../hooks'
import { theme } from '../../../modules/App/Providers/Theme/theme'
import { Appointment, AppointmentTypes, Unavailability, UnavailabilityType } from '../../../types'
import {
  appointmentToTitle,
  appointmentTypeToDisplayText,
  appointmentTypeToPriority,
  getJourneyFromDeliveryModelAndSolution,
  solutionToDisplayText,
  unavailabilityTypeToDisplayText,
} from '../../../util'
import { Select } from '../../inputs'
import { availabilityTheme, MINIMAL_TIME_BLOCK_IN_MINUTES } from '../constants'
import { SyncfusionCalendarEvent } from '../types'
import { getTheme } from './utils'

if (config.SYNCFUSION_LICENSE_KEY) {
  registerLicense(config.SYNCFUSION_LICENSE_KEY)
}

const systemToDisplayText = {
  APPOINTMENT_SERVICE: 'Appointment Service',
  SALESFORCE: 'Salesforce',
}

const activeAppointmentStatuses = [AppointmentStatus.CONFIRMED, AppointmentStatus.PENDING, AppointmentStatus.COMPLETED]

const toResourceStartOfDay = (date?: Date | null) => {
  if (!date) return null
  return zonedTimeToUtc(startOfDay(date), 'UTC')
}

const makeToResourceEndOfDay = (timeZone?: string) => (date?: Date) => date && timeZone ? zonedTimeToUtc(endOfDay(date), timeZone) : null

const appointmentToSyncfusionCalendarEvent = (appointment: Appointment): SyncfusionCalendarEvent => {
  const { duration_in_minutes, id, start_date, type } = appointment
  const startDate = new Date(start_date)

  return {
    appointment,
    AppointmentTypes: [type.appointment_type],
    AvailabilityType: AvailabilityType.AVAILABLE,
    EndTime: addMinutes(startDate, duration_in_minutes),
    EndTimezone: null,
    Id: id,
    IsAllDay: false,
    StartTime: startDate,
    StartTimezone: null,
    Subject: appointmentToTitle({ customer: appointment.customer, type: type.appointment_type }),
  }
}

const unavailabilityToSyncfusionCalendarEvent = (unavailability: Unavailability): SyncfusionCalendarEvent => {
  const start = new Date(unavailability.block.date_start)
  const end = new Date(unavailability.block.date_end)
  const id = start.toISOString()

  return {
    AppointmentTypes: [AppointmentType.HEARING_AID_EVALUATION],
    AvailabilityType: AvailabilityType.UNAVAILABLE,
    EndTime: end,
    EndTimezone: null,
    Id: id,
    IsAllDay: false,
    RecurrenceRule: undefined,
    StartTime: start,
    StartTimezone: null,
    Subject: unavailabilityTypeToDisplayText[unavailability.type as UnavailabilityType],
    unavailability,
  }
}

const Calendar = () => {
  const scheduleRef = useRef<ScheduleComponent>(null)
  const country = useCountry()
  const { resource } = useResource()
  const { t } = useTranslation()
  const { locale } = useLocale()
  const { notify } = useNotification()
  const [activeViewChangedAt, setActiveViewChangedAt] = useState(0)
  const [dateEnd, setDateEnd] = useState<Date | null>(null)
  const [dateStart, setDateStart] = useState<Date | null>(null)
  const [selectedAppointmentType, setSelectedAppointmentType] = useState<AppointmentType | ''>(AppointmentType.HEARING_AID_EVALUATION)
  const [selectedSolution, setSelectedSolution] = useState<NonDeprecatedSolution | ''>(NonDeprecatedSolution.HEAR_TODAY)
  const hasShownErrorRef = useRef(false)
  const resourceTimeZone = resource?.timezone
  const toResourceEndOfDay = useMemo(() => makeToResourceEndOfDay(resourceTimeZone), [resourceTimeZone])
  const { data: shops = [] } = useShops({ id: resource?.shop_id }, { enabled: Boolean(resource?.shop_id) })
  const [shop] = shops

  const nonDeprecatedSolutionsOptions = Object.values(NonDeprecatedSolution).map((solution) => ({
    name: solutionToDisplayText[solution],
    value: solution,
  }))

  const { journey: searchJourney, delivery_model: searchDeliveryModel } = useSearchParameters()
  const deliveryModel = searchDeliveryModel as DeliveryModel
  const journey = (searchJourney ??
    getJourneyFromDeliveryModelAndSolution(deliveryModel, selectedSolution as NonDeprecatedSolution)) as Journey

  const { appointmentTypes = {} } = useAppointmentTypeOptions(
    {
      country: shop?.country ?? country,
      journey,
    },
    { enabled: Boolean(shop?.country) }
  )

  const appointmentTypesHash = JSON.stringify(appointmentTypes)
  const isAvailabilitiesViewQueryEnabled = Boolean(selectedAppointmentType && resource?.id && dateStart && dateEnd && dateEnd > dateStart)

  const {
    data: unavailabilitiesResponse = {},
    isError: haveUnavailabilitiesErrored,
    isFetched,
  } = useAvailabilitiesView(
    {
      appointmentType: selectedAppointmentType as AppointmentType,
      dateEnd: dateEnd as Date,
      dateStart: dateStart as Date,
      durationInMinutes: 60,
      journey,
      resourceId: resource?.id as string,
      resourceTimeZone: resourceTimeZone as string,
    },
    { enabled: isAvailabilitiesViewQueryEnabled }
  )

  const unavailabilitiesForResource = Object.values(unavailabilitiesResponse)[0] ?? []
  const unavailabilitiesForResourceHash = JSON.stringify(unavailabilitiesForResource)

  const appointmentTypeOptions = useMemo(
    () =>
      Object.values(appointmentTypes as AppointmentTypes)
        // .filter((appointmentType) => appointmentType.)
        .map((config) => {
          const name = appointmentTypeToDisplayText[config.appointment_type]

          return {
            name,
            value: config.appointment_type,
            priority: appointmentTypeToPriority[config.appointment_type],
          }
        })
        .sort((a, b) => a.priority - b.priority),
    [appointmentTypesHash]
  )

  const appointments: Appointment[] = useMemo(
    () =>
      unavailabilitiesForResource
        .filter(
          ({ related_entity, type }) =>
            type === UnavailabilityType.EXISTING_APPOINTMENT &&
            activeAppointmentStatuses.includes(related_entity?.appointment?.status?.status as AppointmentStatus)
        )
        .map(({ related_entity }) => related_entity?.appointment as Appointment),
    [unavailabilitiesForResourceHash]
  )

  const appointmentsHash = JSON.stringify(appointments)

  const unavailabilities: Unavailability[] = useMemo(
    () => unavailabilitiesForResource.filter(({ type }) => type !== UnavailabilityType.EXISTING_APPOINTMENT),
    [unavailabilitiesForResource.length]
  )

  const unavailabilitiesHash = JSON.stringify(unavailabilities)

  useEffect(() => {
    let timeout: NodeJS.Timeout

    if (isFetched) {
      timeout = setTimeout(() => scheduleRef.current?.hideSpinner(), 1_000)

      return scheduleRef.current?.hideSpinner()
    }

    timeout = setTimeout(() => scheduleRef.current?.showSpinner(), 0)

    return () => clearTimeout(timeout)
  }, [isFetched])

  useEffect(() => {
    const timeout = setTimeout(() => {
      const currentViewDates = scheduleRef.current?.getCurrentViewDates() ?? []
      const [start, end] = [currentViewDates[0], currentViewDates[currentViewDates.length - 1]]

      if (!start || !end) {
        return
      }

      setDateStart(toResourceStartOfDay(start))
      setDateEnd(toResourceEndOfDay(end))
    }, 0)

    return () => clearTimeout(timeout)
  }, [activeViewChangedAt, toResourceEndOfDay, toResourceStartOfDay])

  useEffect(() => {
    if (hasShownErrorRef.current) {
      return
    }

    if (haveUnavailabilitiesErrored) {
      notify(
        {
          message: 'We are not able to retrieve unavailabilities for this specialist',
          type: ToastSubType.UNAVAILABILITIES_NOT_SUPPORTED,
        },
        'error',
        { persist: true }
      )

      hasShownErrorRef.current = true

      return
    }
  }, [haveUnavailabilitiesErrored, notify])

  const calendarEvents: SyncfusionCalendarEvent[] = useMemo(() => {
    const appointmentEvents: SyncfusionCalendarEvent[] = appointments.map(appointmentToSyncfusionCalendarEvent)
    const unavailabilityEvents: SyncfusionCalendarEvent[] = unavailabilities.map(unavailabilityToSyncfusionCalendarEvent)

    return [...appointmentEvents, ...unavailabilityEvents]
  }, [dateEnd, dateStart, appointmentsHash, unavailabilitiesHash])

  const onEventRendered = useCallback(
    (args: any) => {
      const { element } = args
      const data = args?.data as SyncfusionCalendarEvent

      const availability = data?.AvailabilityType as AvailabilityType | undefined
      const appointmentTypes = data?.AppointmentTypes ?? []

      if (!element || !availability) {
        return
      }

      const theme = getTheme(availability, appointmentTypes)

      element.style.borderColor = `${theme.accent}33`
      element.style.backgroundColor = theme.background
      element.style.color = availabilityTheme[availability].foreground

      const locationEl = element.querySelector('.e-location') as HTMLDivElement

      locationEl.style.display = 'none'
    },
    [theme]
  )

  const onNavigating = useCallback(() => setActiveViewChangedAt(new Date().getTime()), [setActiveViewChangedAt])

  const onPopupOpen = useCallback(
    (args: PopupOpenEventArgs) => {
      const { data: tmpData, element, type } = args
      const { appointment, unavailability } = tmpData as SyncfusionCalendarEvent

      if (type !== 'QuickInfo' || !(appointment || unavailability)) {
        args.cancel = true

        return
      }

      if (appointment) {
        const { created_at, created_by: createdBy } = appointment
        const createdAt = new Date(created_at)

        let author = ''

        if (createdBy.type === 'USER') {
          author = createdBy?.email ?? 'User'
        } else if (createdBy?.system) {
          author = systemToDisplayText[createdBy.system as keyof typeof systemToDisplayText] ?? createdBy.system
        }

        const container = element.querySelector('.e-popup-content')

        const div = document.createElement('div')
        const divAuthor = document.createElement('div')
        const divCreatedAt = document.createElement('div')

        author = author ? `Created by: ${author}` : ''

        divAuthor.innerText = author

        const dateFormatter = new Intl.DateTimeFormat(locale, { dateStyle: 'long', timeZone: resourceTimeZone })
        const timeFormatter = new Intl.DateTimeFormat(locale, { timeStyle: 'short', timeZone: resourceTimeZone })

        divCreatedAt.innerText = `Created at: ${dateFormatter.format(createdAt)} ${timeFormatter.format(createdAt)}`

        div.style.display = 'grid'
        div.style.rowGap = '0.5rem'
        div.style.fontSize = '0.875rem'
        div.style.margin = '0.5rem 0 0'

        div.appendChild(divCreatedAt)

        author && div.appendChild(divAuthor)

        container?.appendChild(div)

        return
      }

      if (unavailability) {
        return
      }
    },
    [locale, resourceTimeZone]
  )

  const onSelectedAppointmentTypeChange = useCallback(
    (event: SelectChangeEvent<AppointmentType>) => {
      const newAppointmentType = event.target.value as AppointmentType

      setSelectedAppointmentType(newAppointmentType)
    },
    [setSelectedAppointmentType]
  )

  const onSelectedSolutionChange = useCallback(
    (event: SelectChangeEvent<NonDeprecatedSolution>) => {
      const newSolution = event.target.value as NonDeprecatedSolution

      setSelectedSolution(newSolution)
    },
    [setSelectedSolution]
  )

  return (
    <>
      <GlobalStyles
        styles={{
          '.e-btn.e-primary': {
            backgroundColor: theme.palette.primary.light,
            '&:hover': {
              backgroundColor: theme.palette.primary.main,
            },
            '&.e-active': {
              backgroundColor: theme.palette.primary.main,
            },
            '&.e-flat': {
              color: theme.palette.primary.main,
              '&:hover': {
                color: theme.palette.primary.main,
                backgroundColor: `${theme.palette.primary.main}0a`,
              },
            },
          },
          '.e-input-group.e-control-wrapper.e-valid-input.e-datetime-wrapper.e-input-focus::before': {
            backgroundColor: theme.palette.primary.main,
          },
          '.e-input-group.e-control-wrapper.e-valid-input.e-datetime-wrapper.e-input-focus::after': {
            backgroundColor: theme.palette.primary.main,
          },
          '.e-subject': {
            color: `${theme.palette.common.black} !important`,
          },
          'button.e-close': {
            color: `${theme.palette.common.black} !important`,
          },
          'button.e-edit': {
            display: 'none !important',
          },
          'button.e-delete': {
            display: 'none !important',
          },
        }}
      />
      <div style={{ display: 'flex', gap: '1rem' }}>
        <div style={{ width: '10rem' }}>
          <Select
            defaultValue={Solution.HEAR_TODAY}
            label={t('common.label.solution')}
            name="solution"
            // @ts-expect-error ???
            onChange={onSelectedSolutionChange}
            options={nonDeprecatedSolutionsOptions}
            value={nonDeprecatedSolutionsOptions ? selectedSolution : ''}
          />
        </div>
        <div style={{ width: '12rem' }}>
          <Select
            defaultValue={appointmentTypeOptions.length ? AppointmentType.HEARING_AID_EVALUATION : ''}
            label={t('common.label.appointmentType')}
            name="appointment-type"
            // @ts-expect-error ???
            onChange={onSelectedAppointmentTypeChange}
            options={appointmentTypeOptions}
            value={appointmentTypeOptions ? selectedAppointmentType : ''}
          />
        </div>
      </div>
      <ScheduleComponent
        endHour="22:00"
        eventRendered={onEventRendered}
        eventSettings={{ dataSource: calendarEvents }}
        height="800px"
        navigating={onNavigating}
        popupOpen={onPopupOpen}
        readOnly
        ref={scheduleRef}
        quickInfoOnSelectionEnd={false}
        showQuickInfo
        startHour="05:00"
        timeScale={{ interval: MINIMAL_TIME_BLOCK_IN_MINUTES * 2 }}
        timezone={resourceTimeZone}
        views={['Week', 'WorkWeek']}
        width="auto"
        workHours={{ highlight: true, start: '08:00', end: '18:00' }}
      >
        <Inject services={[Week, WorkWeek]} />
      </ScheduleComponent>
    </>
  )
}

export { Calendar }
